import { parseLogisticsFromBarcodes } from "./logisticsCarrierRules";

const MAX_IMAGE_WIDTH = 2000;
const HORIZONTAL_STRIPS = 5;
const VERTICAL_STRIPS = 3;
const GRID_ROWS = 2;
const GRID_COLS = 3;

let zbarModulePromise = null;
let zxingModulePromise = null;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("图片加载失败"));
    };
    img.src = url;
  });
}

function getScaledSize(width, height, maxWidth) {
  if (width <= maxWidth) {
    return { width, height };
  }
  const ratio = maxWidth / width;
  return {
    width: maxWidth,
    height: Math.round(height * ratio),
  };
}

function drawImageToCanvas(img, width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

function getZbarModule() {
  if (!zbarModulePromise) {
    // inlined/index.js 的 UMD 打包不会导出 scanImageData，动态 import 得到空对象
    zbarModulePromise = import(
      /* webpackChunkName: "zbar-wasm" */ "../../node_modules/@undecaf/zbar-wasm/dist/inlined/main.cjs"
    );
  }
  return zbarModulePromise;
}

function resolveScanImageData(mod) {
  if (!mod) return null;
  if (typeof mod.scanImageData === "function") return mod.scanImageData;
  if (mod.default && typeof mod.default.scanImageData === "function") return mod.default.scanImageData;
  return null;
}

function getZxingModule() {
  if (!zxingModulePromise) {
    zxingModulePromise = import(/* webpackChunkName: "zxing-library" */ "@zxing/library");
  }
  return zxingModulePromise;
}

async function createZxingHints() {
  const { BarcodeFormat, DecodeHintType } = await getZxingModule();
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.ITF,
  ]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return hints;
}

async function scanWithZbar(imageData) {
  const mod = await getZbarModule();
  const scanImageData = resolveScanImageData(mod);
  if (!scanImageData) {
    throw new Error("ZBar scanImageData 不可用");
  }
  const symbols = await scanImageData(imageData);
  return (symbols || [])
    .map(symbol => {
      try {
        return symbol.decode();
      } catch (e) {
        return "";
      }
    })
    .filter(Boolean);
}

async function scanCanvasWithZxing(canvas) {
  const {
    MultiFormatReader,
    BinaryBitmap,
    HybridBinarizer,
    HTMLCanvasElementLuminanceSource,
    NotFoundException,
  } = await getZxingModule();
  const hints = await createZxingHints();
  const reader = new MultiFormatReader();
  reader.setHints(hints);

  try {
    const luminanceSource = new HTMLCanvasElementLuminanceSource(canvas);
    const binaryBitmap = new BinaryBitmap(new HybridBinarizer(luminanceSource));
    const result = reader.decode(binaryBitmap);
    return result && result.getText ? result.getText() : null;
  } catch (e) {
    if (e instanceof NotFoundException) return null;
    return null;
  } finally {
    reader.reset();
  }
}

function createRegionCanvas(sourceCanvas, sx, sy, sw, sh) {
  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas;
}

async function collectZxingBarcodes(canvas) {
  const texts = new Set();
  const width = canvas.width;
  const height = canvas.height;

  const pushText = text => {
    if (text) texts.add(text);
  };

  pushText(await scanCanvasWithZxing(canvas));

  for (let i = 0; i < HORIZONTAL_STRIPS; i++) {
    const stripHeight = Math.ceil(height / HORIZONTAL_STRIPS);
    const y = i * stripHeight;
    const h = Math.min(stripHeight, height - y);
    if (h <= 0) continue;
    const stripCanvas = createRegionCanvas(canvas, 0, y, width, h);
    pushText(await scanCanvasWithZxing(stripCanvas));
  }

  for (let i = 0; i < VERTICAL_STRIPS; i++) {
    const stripWidth = Math.ceil(width / VERTICAL_STRIPS);
    const x = i * stripWidth;
    const w = Math.min(stripWidth, width - x);
    if (w <= 0) continue;
    const stripCanvas = createRegionCanvas(canvas, x, 0, w, height);
    pushText(await scanCanvasWithZxing(stripCanvas));
  }

  const cellWidth = Math.ceil(width / GRID_COLS);
  const cellHeight = Math.ceil(height / GRID_ROWS);
  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      const x = col * cellWidth;
      const y = row * cellHeight;
      const w = Math.min(cellWidth, width - x);
      const h = Math.min(cellHeight, height - y);
      if (w <= 0 || h <= 0) continue;
      const cellCanvas = createRegionCanvas(canvas, x, y, w, h);
      pushText(await scanCanvasWithZxing(cellCanvas));
    }
  }

  return [...texts];
}

/**
 * 从图片 File 扫描全部条形码并解析物流单号
 * @param {File} file
 * @returns {Promise<{ logisticsNos: string[], logisticsCompanies: string[] }>}
 */
export async function scanLogisticsBarcodesFromFile(file) {
  if (!file) {
    return { logisticsNos: [], logisticsCompanies: [] };
  }

  const img = await loadImageFromFile(file);
  const { width, height } = getScaledSize(img.naturalWidth || img.width, img.naturalHeight || img.height, MAX_IMAGE_WIDTH);
  const canvas = drawImageToCanvas(img, width, height);
  const ctx = canvas.getContext("2d");
  const imageData = ctx.getImageData(0, 0, width, height);

  const rawBarcodes = new Set();

  try {
    const zbarTexts = await scanWithZbar(imageData);
    zbarTexts.forEach(text => rawBarcodes.add(text));
  } catch (e) {
    // ZBar 失败时继续 ZXing 兜底
  }

  const zxingTexts = await collectZxingBarcodes(canvas);
  zxingTexts.forEach(text => rawBarcodes.add(text));

  return parseLogisticsFromBarcodes([...rawBarcodes]);
}
